/**
 * Global fetch wrapper.
 *
 * On a 401/403 from an authenticated API call, refresh the next-auth session
 * cookie once (using the stored long-lived device token) and replay the request
 * a single time. The device is online during a normal photobooth session, so a
 * transient stale/rotated cookie should self-heal here instead of surfacing as a
 * permanent "Failed to get upload URL" and a lost session.
 *
 * Auth endpoints (/api/auth/*) and non-API origins (e.g. the S3/R2 PUT) are
 * passed through untouched. The refresh also syncs the fresh token to the main
 * process (via authStore.initializeCookie -> syncTokensToMainProcess), so the
 * background-job uploader recovers on its next retry without a separate fix.
 */

import { useAuthStore } from '../stores/authStore'
import { isNativeShell, isDigitalMode } from '@renderer/lib/runtime'

const API_URL = process.env.VITE_API_URL || ''

type RetryableInit = RequestInit & { __retriedAfterRefresh?: boolean }

/**
 * Digital-mode same-origin rewrite.
 *
 * In digital web mode the SPA is served from `dreambooth.app` while
 * `VITE_API_URL` points at the studio origin (`dreamboothstudio.com`). Those
 * are two different registrable domains, so a credentialed fetch to the studio
 * makes the `project-auth.session-token` cookie a THIRD-PARTY cookie. Chrome
 * (incognito today, everyone as 3P cookies sunset) refuses to send it back, so
 * the session token minted by `/api/projects/token` never reaches the follow-up
 * `/api/projects/version` call — it 401s and the visitor lands on NotFound.
 *
 * Rewriting every studio API call to the SAME origin (proxied through to the
 * studio by the `/api/(.*)` rewrite in `vercel.json`) makes the cookie
 * first-party, so it is stored and sent normally. Kiosk (Electron/Tauri and
 * `app.dreamboothstudio.com`, which is already same-site with the studio) is
 * untouched — `isDigitalMode()` is false there, so this is a pure pass-through.
 */
function rewriteForDigital(input: RequestInfo | URL): RequestInfo | URL {
  if (!API_URL || !isDigitalMode()) return input

  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  if (!url.startsWith(API_URL)) return input

  const sameOrigin = window.location.origin + url.slice(API_URL.length)
  if (typeof input === 'string') return sameOrigin
  if (input instanceof URL) return new URL(sameOrigin)
  // Rebuild the Request against the same-origin URL, preserving method/body/etc.
  return new Request(sameOrigin, input)
}

// Single-flight: a burst of concurrent 401s (photos + strip + gif + mp4 +
// background jobs all firing at session end) share one refresh, not N.
let refreshInFlight: Promise<boolean> | null = null

function refreshSessionOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = useAuthStore
      .getState()
      .initializeCookie()
      .catch((error) => {
        console.warn('[FETCH] Session refresh threw:', error)
        return false
      })
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

export function installFetchInterceptor(): void {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // In digital web mode, redirect studio-origin calls to this same origin so
    // the project-auth cookie stays first-party (see rewriteForDigital).
    input = rewriteForDigital(input)
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

    if (!API_URL || !url.startsWith(API_URL)) {
      return originalFetch(input, init)
    }

    if (url.includes('/api/auth/')) {
      return originalFetch(input, init)
    }

    const response = await originalFetch(input, init)

    const alreadyRetried = Boolean((init as RetryableInit | undefined)?.__retriedAfterRefresh)
    const isAuthFailure = response.status === 401 || response.status === 403

    if (
      isAuthFailure &&
      !alreadyRetried &&
      isNativeShell() &&
      useAuthStore.getState().isAuthenticated
    ) {
      // A Request object's body is single-use and can't be safely replayed, so
      // only retry string/URL inputs (which is every authed API call in this app).
      const canReplay = typeof input === 'string' || input instanceof URL

      console.warn(
        `[FETCH] ${response.status} — session may be stale (${url.replace(API_URL, '')}); ${
          canReplay ? 'refreshing + retrying once' : 'refreshing for next request'
        }`
      )

      const refreshed = await refreshSessionOnce()

      if (refreshed && canReplay) {
        const retryInit: RetryableInit = { ...(init || {}), __retriedAfterRefresh: true }
        return originalFetch(input, retryInit)
      }
    }

    return response
  }
}
